import { Link } from "react-router-dom";
import { MapPin, MessageCircle, Phone } from "lucide-react";
import { WHATSAPP_NUMBER } from "@/lib/socialLinks";

const SALON_PHONE_DISPLAY = "01752 651631";
const SALON_PHONE_TEL = "+441752651631";

const buildWhatsappUrl = (message: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

interface SalonCTAProps {
  variant?: "full" | "compact";
  topic?: string;
}

/**
 * In-post CTA that drives blog readers to book at the Plymouth salon.
 * Used inside salon-category blog posts (and the /salon page itself).
 */
const SalonCTA = ({ variant = "full", topic }: SalonCTAProps) => {
  const waMessage = topic
    ? `Hi Plymhair — I read your blog post about ${topic} and would like to book at the Plymouth salon.`
    : "Hi Plymhair — I'd like to book an appointment at the Plymouth salon.";

  if (variant === "compact") {
    return (
      <aside className="my-8 rounded-xl border border-primary/15 bg-primary/5 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <p className="font-display text-base font-semibold text-foreground">
            Based in Plymouth?
          </p>
          <p className="font-body text-sm text-muted-foreground mt-1">
            Visit our salon for a fitting or consultation.
          </p>
        </div>
        <a
          href={buildWhatsappUrl(waMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-body text-xs font-semibold tracking-wider uppercase px-5 py-2.5 rounded-lg hover:bg-primary/90 transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> Book on WhatsApp
        </a>
      </aside>
    );
  }

  return (
    <aside className="my-12 rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/5 via-background to-primary/10 p-8 md:p-10">
      <div className="flex items-center gap-2 text-primary font-body text-xs font-semibold tracking-widest uppercase mb-3">
        <MapPin className="w-4 h-4" /> Plymhair Salon · Plymouth
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-bold mb-3">
        Want it done properly? <span className="italic font-normal">Visit us in store.</span>
      </h3>
      <p className="font-body text-muted-foreground leading-relaxed max-w-xl mb-6">
        Our Plymouth salon offers private wig fittings, extension installs,
        consultations and NHS appointments — all by an experienced specialist
        in a calm, judgement-free space.
      </p>
      <div className="flex flex-wrap gap-3">
        <a
          href={buildWhatsappUrl(waMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-body text-sm font-semibold tracking-wider uppercase px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> Book on WhatsApp
        </a>
        <a
          href={`tel:${SALON_PHONE_TEL}`}
          className="inline-flex items-center gap-2 border border-primary/30 text-foreground font-body text-sm font-semibold tracking-wider uppercase px-6 py-3 rounded-lg hover:bg-primary/5 transition-colors"
        >
          <Phone className="w-4 h-4" /> {SALON_PHONE_DISPLAY}
        </a>
        <Link
          to="/salon"
          className="inline-flex items-center gap-2 text-primary font-body text-sm font-semibold tracking-wider uppercase px-6 py-3 hover:underline"
        >
          More about the salon →
        </Link>
      </div>
    </aside>
  );
};

export default SalonCTA;
export { SALON_PHONE_DISPLAY, SALON_PHONE_TEL };
